// Servicio de storage para web usando localStorage

const STORAGE_KEYS = {
  TABLE_DATA: '@ots_table_data',
  TABLE_CLEARED: '@ots_table_cleared',
  SELECTED_RESTAURANT: '@selected_restaurant_id',
  SELECTED_RESTAURANT_NAME: '@selected_restaurant_name',
  SELECTED_RESTAURANT_LOGO: '@selected_restaurant_logo',
  SELECTED_RESTAURANT_STATUS: '@selected_restaurant_status',
  SELECTED_RESTAURANT_TIMEZONE: '@selected_restaurant_timezone',
  USER_DATA: 'otsUser',
  THEME_MODE: 'themeMode',
};

// Funciones de storage genéricas
const storage = {
  setItem: (key: string, value: string): Promise<void> => {
    return new Promise((resolve) => {
      try {
        localStorage.setItem(key, value);
        resolve();
      } catch (error) {
        console.error('Error guardando en localStorage:', error);
        resolve();
      }
    });
  },

  getItem: (key: string): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        const value = localStorage.getItem(key);
        resolve(value);
      } catch (error) {
        console.error('Error leyendo de localStorage:', error);
        resolve(null);
      }
    });
  },

  removeItem: (key: string): Promise<void> => {
    return new Promise((resolve) => {
      try {
        localStorage.removeItem(key);
        resolve();
      } catch (error) {
        console.error('Error removiendo de localStorage:', error);
        resolve();
      }
    });
  },

  clear: (): Promise<void> => {
    return new Promise((resolve) => {
      try {
        localStorage.clear();
        resolve();
      } catch (error) {
        console.error('Error limpiando localStorage:', error);
        resolve();
      }
    });
  }
};

// Funciones específicas para datos de mesa
export const tableStorage = {
  saveTableData: async (data: any): Promise<void> => {
    await storage.setItem(STORAGE_KEYS.TABLE_DATA, JSON.stringify(data));
  },

  getTableData: async (): Promise<any | null> => {
    const data = await storage.getItem(STORAGE_KEYS.TABLE_DATA);
    return data ? JSON.parse(data) : null;
  },

  clearTableData: async (): Promise<void> => {
    await storage.removeItem(STORAGE_KEYS.TABLE_DATA);
  },

  setTableCleared: async (): Promise<void> => {
    await storage.setItem(STORAGE_KEYS.TABLE_CLEARED, 'true');
  },

  isTableCleared: async (): Promise<boolean> => {
    const cleared = await storage.getItem(STORAGE_KEYS.TABLE_CLEARED);
    return cleared === 'true';
  },

  clearTableCleared: async (): Promise<void> => {
    await storage.removeItem(STORAGE_KEYS.TABLE_CLEARED);
  }
};

// Funciones específicas para restaurante seleccionado
export const restaurantStorage = {
  saveSelectedRestaurant: async (id: string, name: string, logo?: string, status?: 'open' | 'closed', timezone?: { name: string; offset: number } | null): Promise<void> => {
    await Promise.all([
      storage.setItem(STORAGE_KEYS.SELECTED_RESTAURANT, id),
      storage.setItem(STORAGE_KEYS.SELECTED_RESTAURANT_NAME, name),
      logo ? storage.setItem(STORAGE_KEYS.SELECTED_RESTAURANT_LOGO, logo) : Promise.resolve(),
      status ? storage.setItem(STORAGE_KEYS.SELECTED_RESTAURANT_STATUS, status) : Promise.resolve(),
      timezone ? storage.setItem(STORAGE_KEYS.SELECTED_RESTAURANT_TIMEZONE, JSON.stringify(timezone)) : Promise.resolve(),
    ]);
  },

  getSelectedRestaurant: async (): Promise<{ id: string | null; name: string | null; logo: string | null; status: 'open' | 'closed' | null; timezone: { name: string; offset: number } | null }> => {
    const [id, name, logo, status, timezoneRaw] = await Promise.all([
      storage.getItem(STORAGE_KEYS.SELECTED_RESTAURANT),
      storage.getItem(STORAGE_KEYS.SELECTED_RESTAURANT_NAME),
      storage.getItem(STORAGE_KEYS.SELECTED_RESTAURANT_LOGO),
      storage.getItem(STORAGE_KEYS.SELECTED_RESTAURANT_STATUS),
      storage.getItem(STORAGE_KEYS.SELECTED_RESTAURANT_TIMEZONE),
    ]);
    let timezone: { name: string; offset: number } | null = null;
    try {
      timezone = timezoneRaw ? JSON.parse(timezoneRaw) : null;
    } catch {
      timezone = null;
    }
    return { id, name, logo, status: status as 'open' | 'closed' | null, timezone };
  },

  saveSelectedRestaurantTimezone: async (timezone: { name: string; offset: number } | null): Promise<void> => {
    if (timezone) {
      await storage.setItem(STORAGE_KEYS.SELECTED_RESTAURANT_TIMEZONE, JSON.stringify(timezone));
    } else {
      await storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_TIMEZONE);
    }
  },

  clearSelectedRestaurant: async (): Promise<void> => {
    await Promise.all([
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT),
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_NAME),
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_LOGO),
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_STATUS),
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_TIMEZONE),
    ]);
  }
};

// Funciones específicas para usuario
export const userStorage = {
  saveUser: async (user: any): Promise<void> => {
    await storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  },

  getUser: async (): Promise<any | null> => {
    const data = await storage.getItem(STORAGE_KEYS.USER_DATA);
    return data ? JSON.parse(data) : null;
  },

  clearUser: async (): Promise<void> => {
    await storage.removeItem(STORAGE_KEYS.USER_DATA);
  }
};

// Funciones específicas para tema
export const themeStorage = {
  saveTheme: async (theme: string): Promise<void> => {
    await storage.setItem(STORAGE_KEYS.THEME_MODE, theme);
  },

  getTheme: async (): Promise<string | null> => {
    return await storage.getItem(STORAGE_KEYS.THEME_MODE);
  }
};

// Función para limpiar TODOS los datos de la aplicación
export const clearAllAppData = async (): Promise<void> => {
  try {
    console.log('🧹 Limpiando TODOS los datos de la aplicación...');
    
    // Limpiar datos específicos de mesa
    await Promise.all([
      storage.removeItem(STORAGE_KEYS.TABLE_DATA),
      storage.removeItem(STORAGE_KEYS.TABLE_CLEARED),
      storage.removeItem('tableData'), // Datos directos de mesa
      storage.removeItem('processedURLs'), // URLs procesadas
    ]);

    // Limpiar datos de restaurante
    await Promise.all([
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT),
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_NAME),
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_LOGO),
      storage.removeItem(STORAGE_KEYS.SELECTED_RESTAURANT_STATUS),
    ]);

    // Limpiar datos de usuario
    await storage.removeItem(STORAGE_KEYS.USER_DATA);

    // Limpiar datos de la aplicación (carrito, órdenes, etc.)
    await Promise.all([
      storage.removeItem('app_cart'),
      storage.removeItem('app_orders'),
      storage.removeItem('app_waiter_requests'),
    ]);

    // Limpiar cache de restaurantes
    await Promise.all([
      storage.removeItem('access_restaurants_cache'),
      storage.removeItem('access_restaurants_timestamp'),
      storage.removeItem('user_restaurants_cache'),
      storage.removeItem('user_restaurants_timestamp'),
    ]);

    // Limpiar datos de token
    await storage.removeItem('token_refresh_info');

    // Limpiar todas las keys que empiecen con patrones específicos
    if (typeof window !== 'undefined' && window.localStorage) {
      const allKeys = Object.keys(window.localStorage);
      
      // Limpiar keys que empiecen con patrones específicos
      allKeys.forEach(key => {
        if (
          key.startsWith('@ots_') ||
          key.startsWith('@selected_') ||
          key.startsWith('ots') ||
          key.startsWith('app_') ||
          key.startsWith('table') ||
          key.startsWith('restaurant') ||
          key.startsWith('user_restaurants') ||
          key.startsWith('access_restaurants') ||
          key.startsWith('token_') ||
          key.includes('processedURLs') ||
          key.includes('tableData')
        ) {
          window.localStorage.removeItem(key);
          console.log(`🗑️ Eliminada key: ${key}`);
        }
      });
    }

    console.log('✅ Limpieza completa de datos finalizada');
  } catch (error) {
    console.error('❌ Error durante la limpieza de datos:', error);
  }
};

// Función para debug del storage
export const debugStorage = (): void => {
  if (typeof window !== 'undefined' && window.localStorage) {
    console.log('🔍 Debug del localStorage:');
    const allKeys = Object.keys(window.localStorage);
    
    if (allKeys.length === 0) {
      console.log('✅ localStorage está completamente vacío');
      return;
    }
    
    console.log(`📊 Total de keys en localStorage: ${allKeys.length}`);
    
    allKeys.forEach(key => {
      const value = window.localStorage.getItem(key);
      console.log(`${key}:`, value);
    });
    
    // Verificar si quedan keys relacionadas con la app
    const appKeys = allKeys.filter(key => 
      key.startsWith('@ots_') ||
      key.startsWith('@selected_') ||
      key.startsWith('ots') ||
      key.startsWith('app_') ||
      key.startsWith('table') ||
      key.startsWith('restaurant') ||
      key.startsWith('user_restaurants') ||
      key.startsWith('access_restaurants') ||
      key.startsWith('token_') ||
      key.includes('processedURLs') ||
      key.includes('tableData')
    );
    
    if (appKeys.length > 0) {
      console.warn('⚠️ Quedan keys relacionadas con la app:', appKeys);
    } else {
      console.log('✅ No quedan keys relacionadas con la app');
    }
  }
};

// Guarda datos de mesa asegurando limpieza previa si cambia restaurante o mesa
export const saveTableDataWithCleanup = async (newData: any): Promise<void> => {
  try {
    const previous = await tableStorage.getTableData();

    const isDifferentTableOrRestaurant = !previous ||
      previous.restaurantId !== newData?.restaurantId ||
      previous.tableId !== newData?.tableId;

    if (isDifferentTableOrRestaurant) {
      // Limpiar datos dependientes de la mesa/restaurante
      await Promise.all([
        // carrito y órdenes actuales
        storage.removeItem('app_cart'),
        storage.removeItem('app_orders'),
        storage.removeItem('app_waiter_requests'),
        // compat antiguo
        storage.removeItem('tableData')
      ]);
    }

    // Guardar los nuevos datos de mesa
    await tableStorage.saveTableData(newData);
  } catch (error) {
    console.error('❌ Error en saveTableDataWithCleanup:', error);
    // Fallback: intentar guardar igualmente
    await tableStorage.saveTableData(newData);
  }
};

// Servicio de storage unificado para compatibilidad
export const storageService = {
  ...storage,
  ...tableStorage,
  ...restaurantStorage,
  ...userStorage,
  ...themeStorage,
  saveTableDataWithCleanup,
  clearAllAppData,
  debugStorage
}; 